import { useCallback, useEffect, useRef, useState } from 'react';
import {
  setLocalOutboundVideoTracksEnabled,
  toggleCallCamera,
} from '../utils/callControlUtils';

/**
 * Subscriber-only in-call camera on/off toggle.
 * Mirrors the mic mute queue pattern in CallingScreen for cross-browser reliability.
 *
 * The control layer reports the actual camera-off state after each attempt.
 * This hook syncs UI to that state instead of guessing whether to revert.
 * In-flight restore is aborted on hangup / unmount so it cannot mutate the next call,
 * and on a later OFF tap so getUserMedia is not left running after the user cancelled.
 */
export function useSubscriberCallCameraToggle({
  activeCall,
  localStream,
  enabled = false,
  onToggleSuccess,
}) {
  const [cameraOff, setCameraOff] = useState(false);
  const cameraOffRef = useRef(false);
  const requestedCameraOffRef = useRef(false);
  const appliedCameraOffRef = useRef(false);
  const cameraToggleInFlightRef = useRef(false);
  const cameraRequestSeqRef = useRef(0);
  const cameraOpGenerationRef = useRef(0);
  const cameraOpAbortRef = useRef(new AbortController());

  const syncCameraOff = useCallback((nextValue) => {
    cameraOffRef.current = nextValue;
    setCameraOff(nextValue);
  }, []);

  const abortAndReplaceCameraOp = useCallback(() => {
    try {
      cameraOpAbortRef.current?.abort();
    } catch (_) { }
    cameraOpAbortRef.current = new AbortController();
  }, []);

  const resetCameraState = useCallback(() => {
    cameraOpGenerationRef.current += 1;
    abortAndReplaceCameraOp();
    cameraRequestSeqRef.current += 1;
    cameraToggleInFlightRef.current = false;
    cameraOffRef.current = false;
    requestedCameraOffRef.current = false;
    appliedCameraOffRef.current = false;
    setCameraOff(false);
  }, [abortAndReplaceCameraOp]);

  useEffect(() => {
    resetCameraState();
    return () => {
      cameraOpGenerationRef.current += 1;
      try {
        cameraOpAbortRef.current?.abort();
      } catch (_) { }
    };
  }, [activeCall?.callId, resetCameraState]);

  useEffect(() => {
    if (!enabled || !activeCall || activeCall.callHasEnded?.()) return;
    setLocalOutboundVideoTracksEnabled(activeCall, !cameraOff);
  }, [enabled, activeCall, localStream, cameraOff]);

  useEffect(() => {
    cameraOffRef.current = cameraOff;
  }, [cameraOff]);

  const toggleCamera = useCallback(() => {
    if (!enabled) return;

    cameraRequestSeqRef.current += 1;
    const nextRequested = !requestedCameraOffRef.current;
    requestedCameraOffRef.current = nextRequested;
    syncCameraOff(nextRequested);

    // Cancel an in-flight camera-ON restore when the latest tap is OFF.
    // Keep the same generation so the queued apply can still run the OFF path.
    if (nextRequested && cameraToggleInFlightRef.current) {
      abortAndReplaceCameraOp();
    }

    const applyRequestedCamera = async () => {
      if (cameraToggleInFlightRef.current) return;
      cameraToggleInFlightRef.current = true;
      const applyGeneration = cameraOpGenerationRef.current;
      const signal = cameraOpAbortRef.current?.signal;
      try {
        while (appliedCameraOffRef.current !== requestedCameraOffRef.current) {
          if (applyGeneration !== cameraOpGenerationRef.current) return;

          const seqAtStart = cameraRequestSeqRef.current;
          const targetCameraOff = requestedCameraOffRef.current;
          const result = await toggleCallCamera({
            activeCall,
            cameraOff: !targetCameraOff,
            signal,
            setCameraOff: (value) => {
              if (applyGeneration !== cameraOpGenerationRef.current) return;
              const nextValue =
                typeof value === 'function' ? value(cameraOffRef.current) : value;
              syncCameraOff(nextValue);
            },
          });

          if (applyGeneration !== cameraOpGenerationRef.current) return;

          const actualCameraOff = result?.cameraOff ?? appliedCameraOffRef.current;

          if (result?.aborted) {
            appliedCameraOffRef.current = actualCameraOff;
            if (cameraRequestSeqRef.current === seqAtStart) {
              requestedCameraOffRef.current = actualCameraOff;
              syncCameraOff(actualCameraOff);
            }
            // Break (do not return): a newer OFF tap already replaced the
            // AbortSignal. Returning here would skip the re-queue below and
            // leave the camera on while the UI shows off.
            break;
          }

          if (result?.ok) {
            appliedCameraOffRef.current = actualCameraOff;
            syncCameraOff(actualCameraOff);
            if (typeof onToggleSuccess === 'function') {
              onToggleSuccess();
            }
          } else {
            appliedCameraOffRef.current = actualCameraOff;
            // Same tap sequence: accept failure. A newer tap keeps `requested`.
            if (cameraRequestSeqRef.current === seqAtStart) {
              requestedCameraOffRef.current = actualCameraOff;
              syncCameraOff(actualCameraOff);
            }
          }
        }
      } finally {
        if (applyGeneration === cameraOpGenerationRef.current) {
          cameraToggleInFlightRef.current = false;
        }
      }

      if (
        applyGeneration === cameraOpGenerationRef.current &&
        appliedCameraOffRef.current !== requestedCameraOffRef.current
      ) {
        void applyRequestedCamera();
      }
    };

    void applyRequestedCamera();
  }, [activeCall, abortAndReplaceCameraOp, enabled, onToggleSuccess, syncCameraOff]);

  return {
    cameraOff,
    toggleCamera,
    resetCameraState,
  };
}
